import React, { useEffect, useState } from "react";
import {
  FormControl,
  Button,
  Text,
  Box,
  Flex,
  Heading,
  VStack,
  HStack,
  Badge,
  Icon,
  Avatar,
  Divider,
  SimpleGrid,
  useColorModeValue,
  Tooltip,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from "@chakra-ui/react";
import {
  FaPencilAlt,
  FaCloudUploadAlt,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaUniversity,
  FaCreditCard,
  FaUpload,
  FaCheck,
  FaTimes,
  FaClock,
  FaFileAlt,
} from "react-icons/fa";
import { motion } from "framer-motion";
import useAxios from "axios-hooks";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import { loggedInUserState } from "atoms/loggedInUserState";
import { toastState } from "atoms/toastState";
import logger from "infra/logger";
import { updateUserFinancialDetailsDrawerState } from "atoms/updateUserFinancialDetailsDrawerState";
import UpdateUserFinancialDetailsDrawer from "./components/UpdateUserFinancialDetailsDrawer";

// Motion components
const MotionBox = motion(Box);
const MotionFlex = motion(Flex);

// Glass Morphism Card Component
const GlassMorphismCard = ({ children, ...rest }) => {
  const bgColor = useColorModeValue(
    "rgba(255, 255, 255, 0.8)",
    "rgba(26, 32, 44, 0.8)"
  );
  const borderColor = useColorModeValue(
    "rgba(255, 255, 255, 0.5)",
    "rgba(255, 255, 255, 0.1)"
  );

  return (
    <Box
      bg={bgColor}
      backdropFilter="blur(10px)"
      borderRadius="2xl"
      boxShadow="xl"
      border="1px solid"
      borderColor={borderColor}
      overflow="hidden"
      transition="all 0.3s ease"
      _hover={{ boxShadow: "2xl" }}
      {...rest}
    >
      {children}
    </Box>
  );
};

// Status Badge Component
const StatusBadge = ({ status }) => {
  let color, label, icon;

  switch (status?.toLowerCase()) {
    case "verified":
      color = "green";
      label = "Verified";
      icon = FaCheck;
      break;
    case "rejected":
      color = "red";
      label = "Rejected";
      icon = FaTimes;
      break;
    case "pending":
      color = "yellow";
      label = "Pending";
      icon = FaClock;
      break;
    case "not_initiated":
      color = "blue";
      label = "Not Initiated";
      icon = FaFileAlt;
      break;
    default:
      color = "gray";
      label = "Unknown";
      icon = FaFileAlt;
  }

  return (
    <Badge
      display="flex"
      alignItems="center"
      px={4}
      py={2}
      borderRadius="full"
      colorScheme={color}
      variant="subtle"
      gap={2}
    >
      <Icon as={icon} boxSize={3} />
      <Text fontSize="sm" fontWeight="medium">
        {label}
      </Text>
    </Badge>
  );
};

const AccountSettings = () => {
  const [userFinancialDetailsDrawerState, setUserFinancialDetailsDrawerState] =
    useRecoilState(updateUserFinancialDetailsDrawerState);
  const loggedInUser = useRecoilValue(loggedInUserState);
  const setToast = useSetRecoilState(toastState);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);

  // Theme colors
  const textColor = useColorModeValue("gray.700", "white");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
  const hoverBg = useColorModeValue("gray.50", "gray.700");
  const themeColor = "#23A979";
  const cardBg = useColorModeValue("white", "gray.800");

  const [userDetails, setUserDetails] = useState({
    BeneficiaryName: "",
    Name: "",
    Email: "",
    MobileNumber: "",
    PanCard: "",
    BankAccountNo: "",
    BankName: "",
    IfscCode: "",
    GstNo: "",
    VerificationStatus: "",
  });

  const [{ data, loading, error }, execute, cancelRequest] = useAxios(
    {
      url: `/api/user/profile/${loggedInUser?.userId}`,
      method: "GET",
    },
    { manual: true }
  );

  const [
    { data: uploadData, loading: Uploadloading, error: Uploaderror },
    UploadDocument,
  ] = useAxios(
    {
      url: `/api/user/upload_undertaking_document/${loggedInUser?.user?.Id}`,
      method: "POST",
      headers: { "Content-Type": "multipart/form-data" },
    },
    { manual: true }
  );

  const fetchData = async () => {
    try {
      const response = await execute();
      const responseData = response.data.data;
      logger.log(responseData);
      setUserDetails({
        BeneficiaryName: responseData?.BeneficiaryName,
        Name: responseData?.FirstName + " " + responseData?.LastName,
        Email: responseData?.Email,
        MobileNumber: responseData.MobileNumber,
        PanCard: responseData?.PanCard,
        BankAccountNo: responseData?.BankAccountNo,
        BankName: responseData?.BankName,
        IfscCode: responseData?.IfscCode,
        GstNo: responseData?.GstNo,
        VerificationStatus: responseData?.VerificationStatus,
        BusinessName: responseData?.BusinessName,
        BusinessPincode: responseData?.BusinessPincode,
        BusinessState: responseData?.BusinessState,
        BusinessCity: responseData?.BusinessCity,
        BusinessAddress: responseData?.BusinessAddress,
      });
    } catch (error) {
      logger.error("Error fetching tenant details:", error);
      if (error?.code !== "ERR_CANCELED") {
        setToast({
          isOpen: true,
          title: "Error Occured",
          status: "error",
          description: error?.response?.data?.error,
        });
      }
    }
  };

  const status = userDetails?.VerificationStatus;
  let color = "black.500";
  let statusText = "";
  switch (status) {
    case "verified":
      statusText = "Verified";
      color = "green.300";
      break;
    case "rejected":
      statusText = "Rejected";
      color = "red.300";
      break;
    case "pending":
      statusText = "Pending";
      color = "brand.300";
      break;
    case "not_initiated":
      statusText = "Not Initiated";
      color = "blue.300";
      break;
    default:
      statusText = "N/A";
      color = "gray.300";
      break;
  }
  const handleInput = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const acceptedFileTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];

    if (!acceptedFileTypes.includes(file.type)) {
      setToast({
        isOpen: true,
        title: "Invalid file type",
        description: "Please upload a PDF or DOC file",
        status: "error",
      });
      return;
    }
    setSelectedFile(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);

    const droppedFiles = e.dataTransfer.files;
    const file = droppedFiles[0];

    if (!file) return;

    const acceptedFileTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];

    if (acceptedFileTypes.includes(file.type)) {
      setSelectedFile(file);
      setToast({
        isOpen: true,
        title: "File Selected",
        description: file.name,
        status: "success",
      });
    } else {
      setToast({
        isOpen: true,
        title: "Invalid file type",
        description: "Please upload a PDF or DOC file",
        status: "error",
      });
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleUpload = () => {
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = ".pdf,.doc,.docx";
    fileInput.style.display = "none";

    fileInput.addEventListener("change", (event) => {
      const file = event.target.files[0];
      if (
        file.type === "application/pdf" ||
        file.type === "application/.doc" ||
        file.type === "application/.docx" ||
        file.type ===
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      ) {
        setSelectedFile(file);
        logger.log("Selected file:", file);
      } else {
        // alert('Please select a PDF or DOC file.');
      }
    });

    fileInput.click();
  };
  const uploadHandler = async () => {
    if (!selectedFile) {
      setToast({
        isOpen: true,
        title: "No file selected",
        description: "Please select a file to upload",
        status: "warning",
      });
      return;
    }

    // Check file size (150 KB limit)
    const maxSizeInBytes = 150 * 1024;
    if (selectedFile.size > maxSizeInBytes) {
      setToast({
        isOpen: true,
        title: "File size exceeds limit",
        description: "Please select a file smaller than 150 KB",
        status: "warning",
      });
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      const response = await UploadDocument({ data: formData });
      setToast({
        isOpen: true,
        title: "File uploaded successfully",
        status: "success",
      });
      setSelectedFile(null);
      fetchData(); // Refresh user data
    } catch (error) {
      const err = error?.response?.data?.error;
      setToast({
        isOpen: true,
        title: "Upload failed",
        description: err || "An error occurred during upload",
        status: "error",
      });
    }
  };

  const update = () => {
    setUserFinancialDetailsDrawerState({ isOpen: false });
    fetchData(); // fetch data
  };

  const updateAcc = () => {
    setUserFinancialDetailsDrawerState({ isOpen: false });
    fetchData();
  };
  const editAccountHandler = () => {
    setUserFinancialDetailsDrawerState({ isOpen: true });
  };

  // Check if verification status is verified
  const isVerified =
    userDetails?.VerificationStatus?.toLowerCase() === "verified";

  useEffect(() => {
    fetchData();
    return () => cancelRequest();
  }, []);
  return (
    <MotionBox
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      w="100%"
      maxW="7xl"
      mx="auto"
      p={6}
    >
      <VStack spacing={8} w="100%">
        {/* Header Section */}
        <MotionFlex
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          w="100%"
          justify="space-between"
          align="center"
        >
          <Box>
            <Heading
              size="lg"
              color={textColor}
              fontWeight="bold"
              letterSpacing="tight"
            >
              Account Settings
            </Heading>
            <Text color="gray.500" fontSize="md" mt={1}>
              Manage your profile and financial information
            </Text>
          </Box>
          <Avatar
            size="lg"
            name={userDetails?.Name}
            bg={themeColor}
            color="white"
            fontSize="xl"
          />
        </MotionFlex>

        {/* User Profile Card */}
        <MotionBox
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          w="100%"
        >
          <GlassMorphismCard p={8}>
            <Flex justify="space-between" align="center" mb={6}>
              <Heading size="md" color={textColor} fontWeight="semibold">
                Profile Information
              </Heading>
              {/* <Button
                size="sm"
                colorScheme="brand"
                variant="outline"
                leftIcon={<Icon as={FaPencilAlt} />}
                _hover={{ transform: "translateY(-2px)", shadow: "md" }}
                transition="all 0.2s"
              >
                Edit Profile
              </Button> */}
            </Flex>

            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
              <Box>
                <HStack spacing={3} mb={2}>
                  <Icon as={FaUser} color={themeColor} boxSize={5} />
                  <Text fontSize="sm" color="gray.500" fontWeight="medium">
                    Full Name
                  </Text>
                </HStack>
                <Text fontSize="md" color={textColor} fontWeight="semibold">
                  {userDetails?.Name || "N/A"}
                </Text>
              </Box>

              <Box>
                <HStack spacing={3} mb={2}>
                  <Icon as={FaEnvelope} color={themeColor} boxSize={5} />
                  <Text fontSize="sm" color="gray.500" fontWeight="medium">
                    Email Address
                  </Text>
                </HStack>
                <Text fontSize="md" color={textColor} fontWeight="semibold">
                  {userDetails?.Email || "N/A"}
                </Text>
              </Box>
              <Box>
                <HStack spacing={3} mb={2}>
                  <Icon as={FaPhone} color={themeColor} boxSize={5} />
                  <Text fontSize="sm" color="gray.500" fontWeight="medium">
                    Phone Number
                  </Text>
                </HStack>
                <Text fontSize="md" color={textColor} fontWeight="semibold">
                  {userDetails?.MobileNumber || "N/A"}
                </Text>
              </Box>
            </SimpleGrid>
          </GlassMorphismCard>
        </MotionBox>

        {/* Bank Account Details Card */}
        <MotionBox
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          w="100%"
        >
          <GlassMorphismCard p={8}>
            <Flex justify="space-between" align="center" mb={6}>
              <Heading size="md" color={textColor} fontWeight="semibold">
                Bank Account Details
              </Heading>
              <Tooltip
                label={
                  isVerified
                    ? "Account is verified. Contact Zoop to make changes."
                    : "Edit account details"
                }
                placement="top"
              >
                <Button
                  size="sm"
                  colorScheme="brand"
                  variant="outline"
                  leftIcon={<Icon as={FaPencilAlt} />}
                  onClick={editAccountHandler}
                  isDisabled={isVerified}
                  _hover={
                    !isVerified
                      ? { transform: "translateY(-2px)", shadow: "md" }
                      : {}
                  }
                  transition="all 0.2s"
                  opacity={isVerified ? 0.6 : 1}
                >
                  Edit Details
                </Button>
              </Tooltip>
            </Flex>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
              <Box>
                <HStack spacing={3} mb={2}>
                  <Icon as={FaUser} color={themeColor} boxSize={5} />
                  <Text fontSize="sm" color="gray.500" fontWeight="medium">
                    Account Holder
                  </Text>
                </HStack>
                <Text fontSize="md" color={textColor} fontWeight="semibold">
                  {userDetails?.BeneficiaryName || "N/A"}
                </Text>
              </Box>

              <Box>
                <HStack spacing={3} mb={2}>
                  <Icon as={FaUniversity} color={themeColor} boxSize={5} />
                  <Text fontSize="sm" color="gray.500" fontWeight="medium">
                    Bank Name
                  </Text>
                </HStack>
                <Text fontSize="md" color={textColor} fontWeight="semibold">
                  {userDetails?.BankName || "N/A"}
                </Text>
              </Box>

              <Box>
                <HStack spacing={3} mb={2}>
                  <Icon as={FaCreditCard} color={themeColor} boxSize={5} />
                  <Text fontSize="sm" color="gray.500" fontWeight="medium">
                    Account Number
                  </Text>
                </HStack>
                <Text fontSize="md" color={textColor} fontWeight="semibold">
                  {userDetails?.BankAccountNo
                    ? `****${userDetails.BankAccountNo.slice(-4)}`
                    : "N/A"}
                </Text>
              </Box>

              <Box>
                <HStack spacing={3} mb={2}>
                  <Icon as={FaUniversity} color={themeColor} boxSize={5} />
                  <Text fontSize="sm" color="gray.500" fontWeight="medium">
                    IFSC Code
                  </Text>
                </HStack>
                <Text fontSize="md" color={textColor} fontWeight="semibold">
                  {userDetails?.IfscCode || "N/A"}
                </Text>
              </Box>

              <Box>
                <HStack spacing={3} mb={2}>
                  <Icon as={FaFileAlt} color={themeColor} boxSize={5} />
                  <Text fontSize="sm" color="gray.500" fontWeight="medium">
                    PAN Card
                  </Text>
                </HStack>
                <Text fontSize="md" color={textColor} fontWeight="semibold">
                  {userDetails?.PanCard || "N/A"}
                </Text>
              </Box>

              <Box>
                <HStack spacing={3} mb={2}>
                  <Icon as={FaFileAlt} color={themeColor} boxSize={5} />
                  <Text fontSize="sm" color="gray.500" fontWeight="medium">
                    GST Number
                  </Text>
                </HStack>
                <Text fontSize="md" color={textColor} fontWeight="semibold">
                  {userDetails?.GstNo || "N/A"}
                </Text>
              </Box>
            </SimpleGrid>
          </GlassMorphismCard>
        </MotionBox>

        {/* Business Details Card */}
        <MotionBox
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.35 }}
          w="100%"
        >
          <GlassMorphismCard p={8}>
            <Flex justify="space-between" align="center" mb={6}>
              <Heading size="md" color={textColor} fontWeight="semibold">
                Business Details
              </Heading>
              <Tooltip
                label={
                  isVerified
                    ? "Account is verified. Contact Zoop to make changes."
                    : "Edit business details"
                }
                placement="top"
              >
                <Button
                  size="sm"
                  colorScheme="brand"
                  variant="outline"
                  leftIcon={<Icon as={FaPencilAlt} />}
                  onClick={editAccountHandler}
                  isDisabled={isVerified}
                  _hover={
                    !isVerified
                      ? { transform: "translateY(-2px)", shadow: "md" }
                      : {}
                  }
                  transition="all 0.2s"
                  opacity={isVerified ? 0.6 : 1}
                >
                  Edit Details
                </Button>
              </Tooltip>
            </Flex>

            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
              <Box>
                <HStack spacing={3} mb={2}>
                  <Icon as={FaUniversity} color={themeColor} boxSize={5} />
                  <Text fontSize="sm" color="gray.500" fontWeight="medium">
                    Business Name
                  </Text>
                </HStack>
                <Text fontSize="md" color={textColor} fontWeight="semibold">
                  {userDetails?.BusinessName || "N/A"}
                </Text>
              </Box>

              <Box>
                <HStack spacing={3} mb={2}>
                  <Icon as={FaFileAlt} color={themeColor} boxSize={5} />
                  <Text fontSize="sm" color="gray.500" fontWeight="medium">
                    Business Pincode
                  </Text>
                </HStack>
                <Text fontSize="md" color={textColor} fontWeight="semibold">
                  {userDetails?.BusinessPincode || "N/A"}
                </Text>
              </Box>

              <Box>
                <HStack spacing={3} mb={2}>
                  <Icon as={FaFileAlt} color={themeColor} boxSize={5} />
                  <Text fontSize="sm" color="gray.500" fontWeight="medium">
                    Business State
                  </Text>
                </HStack>
                <Text fontSize="md" color={textColor} fontWeight="semibold">
                  {userDetails?.BusinessState || "N/A"}
                </Text>
              </Box>

              <Box>
                <HStack spacing={3} mb={2}>
                  <Icon as={FaFileAlt} color={themeColor} boxSize={5} />
                  <Text fontSize="sm" color="gray.500" fontWeight="medium">
                    Business City
                  </Text>
                </HStack>
                <Text fontSize="md" color={textColor} fontWeight="semibold">
                  {userDetails?.BusinessCity || "N/A"}
                </Text>
              </Box>

              <Box gridColumn={{ base: "1", md: "1 / -1", lg: "1 / -1" }}>
                <HStack spacing={3} mb={2}>
                  <Icon as={FaFileAlt} color={themeColor} boxSize={5} />
                  <Text fontSize="sm" color="gray.500" fontWeight="medium">
                    Business Address
                  </Text>
                </HStack>
                <Text
                  fontSize="md"
                  color={textColor}
                  fontWeight="semibold"
                  noOfLines={2}
                  wordBreak="break-word"
                >
                  {userDetails?.BusinessAddress || "N/A"}
                </Text>
              </Box>
            </SimpleGrid>
          </GlassMorphismCard>
        </MotionBox>

        {/* Document Verification Card */}
        <MotionBox
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          w="100%"
        >
          <GlassMorphismCard p={8}>
            <VStack spacing={6} w="100%">
              <Flex justify="space-between" align="center" w="100%">
                <Heading size="md" color={textColor} fontWeight="semibold">
                  Document Verification
                </Heading>
                <StatusBadge status={userDetails?.VerificationStatus} />
              </Flex>
              {/* Verified Account Alert */}
              {isVerified && (
                <Alert status="success" borderRadius="xl">
                  <AlertIcon />
                  <Box>
                    <AlertTitle fontSize="sm">Account Verified!</AlertTitle>
                    <AlertDescription fontSize="sm">
                      Your account has been successfully verified. To make any
                      changes to your details or upload new documents, please
                      contact Zoop support team.
                    </AlertDescription>
                  </Box>
                </Alert>
              )}
              <Divider /> {/* Verification Steps */}
              <Box w="100%">
                <Text
                  fontSize="md"
                  color={textColor}
                  mb={4}
                  fontWeight="medium"
                >
                  Verification Progress
                </Text>
                <Flex
                  direction={{ base: "column", md: "row" }}
                  gap={4}
                  align="center"
                >
                  {/* Step 1: Not Initiated/Rejected */}
                  <VStack flex={1} spacing={2}>
                    <Box
                      w="40px"
                      h="40px"
                      borderRadius="full"
                      bg={
                        userDetails?.VerificationStatus === "rejected"
                          ? "red.500"
                          : userDetails?.VerificationStatus ===
                              "not_initiated" ||
                            !userDetails?.VerificationStatus
                          ? "gray.300"
                          : "green.500"
                      }
                      color="white"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      fontWeight="bold"
                      fontSize="sm"
                    >
                      {userDetails?.VerificationStatus === "rejected" ? (
                        <Icon as={FaTimes} />
                      ) : (
                        "1"
                      )}
                    </Box>
                    <Text
                      fontSize="sm"
                      color={textColor}
                      textAlign="center"
                      fontWeight="medium"
                    >
                      {userDetails?.VerificationStatus === "rejected"
                        ? "Rejected"
                        : "Document Upload"}
                    </Text>
                    <Text fontSize="xs" color="gray.500" textAlign="center">
                      {userDetails?.VerificationStatus === "rejected"
                        ? "Please resubmit documents"
                        : "Upload verification documents"}
                    </Text>
                  </VStack>

                  {/* Connector Line 1 */}
                  <Box
                    w={{ base: "2px", md: "60px" }}
                    h={{ base: "20px", md: "2px" }}
                    bg={
                      userDetails?.VerificationStatus === "pending" ||
                      userDetails?.VerificationStatus === "verified"
                        ? "green.500"
                        : "gray.300"
                    }
                    borderRadius="full"
                  />

                  {/* Step 2: Pending */}
                  <VStack flex={1} spacing={2}>
                    <Box
                      w="40px"
                      h="40px"
                      borderRadius="full"
                      bg={
                        userDetails?.VerificationStatus === "pending"
                          ? "brand.500"
                          : userDetails?.VerificationStatus === "verified"
                          ? "green.500"
                          : "gray.300"
                      }
                      color="white"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      fontWeight="bold"
                      fontSize="sm"
                    >
                      {userDetails?.VerificationStatus === "pending" ? (
                        <Icon as={FaClock} />
                      ) : userDetails?.VerificationStatus === "verified" ? (
                        <Icon as={FaCheck} />
                      ) : (
                        "2"
                      )}
                    </Box>
                    <Text
                      fontSize="sm"
                      color={textColor}
                      textAlign="center"
                      fontWeight="medium"
                    >
                      Under Review
                    </Text>
                    <Text fontSize="xs" color="gray.500" textAlign="center">
                      Documents being verified
                    </Text>
                  </VStack>

                  {/* Connector Line 2 */}
                  <Box
                    w={{ base: "2px", md: "60px" }}
                    h={{ base: "20px", md: "2px" }}
                    bg={
                      userDetails?.VerificationStatus === "verified"
                        ? "green.500"
                        : "gray.300"
                    }
                    borderRadius="full"
                  />

                  {/* Step 3: Verified */}
                  <VStack flex={1} spacing={2}>
                    <Box
                      w="40px"
                      h="40px"
                      borderRadius="full"
                      bg={
                        userDetails?.VerificationStatus === "verified"
                          ? "green.500"
                          : "gray.300"
                      }
                      color="white"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      fontWeight="bold"
                      fontSize="sm"
                    >
                      {userDetails?.VerificationStatus === "verified" ? (
                        <Icon as={FaCheck} />
                      ) : (
                        "3"
                      )}
                    </Box>
                    <Text
                      fontSize="sm"
                      color={textColor}
                      textAlign="center"
                      fontWeight="medium"
                    >
                      Verified
                    </Text>
                    <Text fontSize="xs" color="gray.500" textAlign="center">
                      Account fully verified
                    </Text>
                  </VStack>
                </Flex>
              </Box>
              {/* Conditional Content Based on Verification Status */}
              {isVerified ? (
                /* Verified Status Placeholder */
                <Box w="100%">
                  <Flex
                    bg={hoverBg}
                    p={8}
                    borderRadius="xl"
                    direction="column"
                    align="center"
                    justify="center"
                    textAlign="center"
                    minH="200px"
                  >
                    <VStack spacing={4}>
                      <Icon as={FaCheck} boxSize={16} color="green.500" />
                      <Heading
                        size="md"
                        color={textColor}
                        fontWeight="semibold"
                      >
                        Account Successfully Verified
                      </Heading>
                      <Text fontSize="md" color="gray.500" maxW="md">
                        Your account and documents have been verified. All
                        features are now available.
                      </Text>
                      <Text fontSize="sm" color="gray.400">
                        Need to make changes? Contact Zoop support team for
                        assistance.
                      </Text>
                    </VStack>
                  </Flex>
                </Box>
              ) : (
                <>
                  {/* Instructions */}
                  <Box w="100%">
                    <Flex
                      bg={hoverBg}
                      p={6}
                      borderRadius="xl"
                      direction={{ base: "column", lg: "row" }}
                      gap={6}
                      align="center"
                    >
                      {/* Steps Section */}
                      <VStack flex={1} align="start" spacing={4}>
                        <Heading
                          size="sm"
                          color={textColor}
                          fontWeight="semibold"
                        >
                          Quick Setup Process
                        </Heading>
                        <SimpleGrid
                          columns={{ base: 1, md: 2 }}
                          spacing={3}
                          w="100%"
                        >
                          <HStack spacing={3}>
                            <Badge
                              colorScheme="brand"
                              variant="solid"
                              borderRadius="full"
                              minW="24px"
                              h="24px"
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              fontSize="xs"
                            >
                              1
                            </Badge>
                            <Text fontSize="sm" color={textColor}>
                              Download template
                            </Text>
                          </HStack>
                          <HStack spacing={3}>
                            <Badge
                              colorScheme="brand"
                              variant="solid"
                              borderRadius="full"
                              minW="24px"
                              h="24px"
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              fontSize="xs"
                            >
                              2
                            </Badge>
                            <Text fontSize="sm" color={textColor}>
                              Print & fill details
                            </Text>
                          </HStack>
                          <HStack spacing={3}>
                            <Badge
                              colorScheme="brand"
                              variant="solid"
                              borderRadius="full"
                              minW="24px"
                              h="24px"
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              fontSize="xs"
                            >
                              3
                            </Badge>
                            <Text fontSize="sm" color={textColor}>
                              Scan or photograph
                            </Text>
                          </HStack>
                          <HStack spacing={3}>
                            <Badge
                              colorScheme="brand"
                              variant="solid"
                              borderRadius="full"
                              minW="24px"
                              h="24px"
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              fontSize="xs"
                            >
                              4
                            </Badge>
                            <Text fontSize="sm" color={textColor}>
                              Upload completed form
                            </Text>
                          </HStack>
                        </SimpleGrid>
                      </VStack>

                      {/* Download Section */}
                      <VStack spacing={3} align="center">
                      </VStack>
                    </Flex>
                  </Box>

                  {/* Upload Zone */}
                  <Box w="100%" maxW="500px">
                    <FormControl>
                      <Box
                        border="2px dashed"
                        borderColor={isDragOver ? themeColor : borderColor}
                        borderRadius="xl"
                        p={8}
                        textAlign="center"
                        cursor="pointer"
                        transition="all 0.3s ease"
                        bg={isDragOver ? `${themeColor}10` : "transparent"}
                        _hover={{
                          borderColor: themeColor,
                          bg: `${themeColor}05`,
                        }}
                        onDrop={handleDrop}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                      >
                        <VStack spacing={4}>
                          <Icon
                            as={selectedFile ? FaCheck : FaCloudUploadAlt}
                            boxSize={12}
                            color={selectedFile ? "green.500" : themeColor}
                          />
                          <VStack spacing={2}>
                            <Text
                              fontSize="lg"
                              fontWeight="semibold"
                              color={textColor}
                            >
                              {selectedFile
                                ? selectedFile.name
                                : "Drop your file here"}
                            </Text>
                            <Text fontSize="sm" color="gray.500">
                              or click to browse
                            </Text>
                            <Text fontSize="xs" color="gray.400">
                              Supports PDF, DOC files up to 150KB
                            </Text>
                          </VStack>
                          <input
                            type="file"
                            accept=".pdf,.doc,.docx"
                            onChange={handleInput}
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: "100%",
                              opacity: 0,
                              cursor: "pointer",
                            }}
                          />
                        </VStack>
                      </Box>
                    </FormControl>

                    {selectedFile && (
                      <Flex justify="space-between" mt={4} gap={3}>
                        <Button
                          variant="outline"
                          colorScheme="gray"
                          onClick={() => setSelectedFile(null)}
                          flex={1}
                        >
                          Clear
                        </Button>
                        <Button
                          colorScheme="green"
                          leftIcon={<Icon as={FaUpload} />}
                          onClick={uploadHandler}
                          isLoading={Uploadloading}
                          flex={2}
                          _hover={{
                            transform: "translateY(-2px)",
                            shadow: "md",
                          }}
                          transition="all 0.2s"
                        >
                          Upload Document
                        </Button>
                      </Flex>
                    )}
                  </Box>
                </>
              )}
            </VStack>
          </GlassMorphismCard>
        </MotionBox>
      </VStack>

      {/* Drawer */}
      {userFinancialDetailsDrawerState?.isOpen && (
        <UpdateUserFinancialDetailsDrawer update={updateAcc} />
      )}
    </MotionBox>
  );
};

export default AccountSettings;
